import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import useRetryFetch from "../hooks/useRetryFetch";
import { config } from "../Environment";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { CheckCircle2, Loader2, UserRound, AlertCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { PaymentIframe } from "./payment/PaymentIframe";
import { Input } from "./ui/input";
import {
  getSegmentPublicInfo,
  joinSegment,
  guestCheckout,
  SegmentPublicInfo,
} from "../api/mentorSegments";

function JoinMentor() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get("return");
  const { auth } = useAuth();
  const retryFetch = useRetryFetch();

  const [info, setInfo] = useState<SegmentPublicInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);
  const [error, setError] = useState("");

  // Paid checkout (logged-in users)
  const [paymentUrl, setPaymentUrl] = useState("");
  const [showPayment, setShowPayment] = useState(false);
  const [paying, setPaying] = useState(false);

  // Guest checkout (logged-out, paid segment)
  const [guestEmail, setGuestEmail] = useState("");
  const [guestName, setGuestName] = useState("");
  const [guestPaying, setGuestPaying] = useState(false);
  const [isGuestPayment, setIsGuestPayment] = useState(false);

  const isPaid = !!info?.pricing?.priced && info.pricing.amount != null;
  const priceLabel =
    isPaid && info?.pricing
      ? new Intl.NumberFormat(undefined, {
          style: "currency",
          currency: info.pricing.currency || "USD",
        }).format(info.pricing.amount!)
      : "";

  const handleGuestPay = async () => {
    if (!token) return;
    if (!guestEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail.trim())) {
      toast.error(t("enter_valid_email") || "Enter a valid email");
      return;
    }
    try {
      setGuestPaying(true);
      const { redirect } = await guestCheckout(token, guestEmail.trim(), guestName.trim());
      // Open the in-app payment iframe (guest mode) — same UX as every other payment.
      setIsGuestPayment(true);
      setPaymentUrl(redirect);
      setShowPayment(true);
    } catch (err: any) {
      if (err.code === "account_exists") {
        toast.error(
          t("account_exists_login") ||
            "You already have an account — please log in to purchase."
        );
        navigate(`/login?return=${encodeURIComponent(`/join-mentor/${token}`)}`);
      } else {
        toast.error(err.message || t("checkout_failed") || "Could not start checkout");
      }
    } finally {
      setGuestPaying(false);
    }
  };

  const handlePay = async () => {
    if (!info?.pricing?.productId) return;
    if (!auth?.accessToken) {
      navigate(`/login?return=${encodeURIComponent(`/join-mentor/${token}`)}`);
      return;
    }
    try {
      setPaying(true);
      const res = await retryFetch(`${config.API_BASE}/api/payment/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ productId: info.pricing.productId, useIframe: true }),
      });
      const data = await res.json();
      if (!res.ok || !data.redirect) {
        throw new Error(data.error || "Could not start checkout");
      }
      setIsGuestPayment(false);
      setPaymentUrl(data.redirect);
      setShowPayment(true);
    } catch (err: any) {
      toast.error(err.message || t("checkout_failed") || "Could not start checkout");
    } finally {
      setPaying(false);
    }
  };

  useEffect(() => {
    if (!token) {
      setError(t("invalid_join_link") || "Invalid join link");
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await getSegmentPublicInfo(token);
        if (!cancelled) setInfo(data);
      } catch {
        if (!cancelled) setError(t("mentor_link_invalid") || "This link is invalid or no longer active");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  const handleJoin = async () => {
    if (!token) return;
    if (!auth?.accessToken) {
      navigate(`/login?return=${encodeURIComponent(`/join-mentor/${token}`)}`);
      return;
    }
    try {
      setJoining(true);
      const result = await joinSegment(retryFetch, token);
      setJoined(true);
      toast.success(
        result.alreadyMember
          ? t("already_connected_to_mentor") || "You're already connected with this mentor"
          : t("successfully_connected_to_mentor") || "You're now connected with your mentor"
      );
      setTimeout(() => {
        navigate(result.onboardingProgramId ? `/programs/${result.onboardingProgramId}` : "/portal");
      }, 1800);
    } catch (err: any) {
      setError(err.message || t("failed_to_join_mentor") || "Failed to connect with mentor");
      toast.error(err.message || t("failed_to_join_mentor") || "Failed to connect with mentor");
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full w-full flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="text-muted-foreground">{t("loading")}</span>
        </div>
      </div>
    );
  }

  if (error && !info) {
    return (
      <div className="h-full w-full flex items-center justify-center py-12">
        <div className="mx-auto grid w-full max-w-[400px] gap-6 px-4">
          <Card className="border-red-200">
            <CardHeader>
              <div className="flex items-center gap-2 text-red-600">
                <AlertCircle className="h-5 w-5" />
                <CardTitle>{t("error") || "Error"}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">{error}</p>
              <Button onClick={() => navigate("/marketplace")} className="w-full">
                {t("go_to_marketplace") || "Go to Marketplace"}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex items-center justify-center py-12">
      <div className="mx-auto grid w-full max-w-[500px] gap-6 px-4">
        <div className="grid gap-2 text-center">
          <h1 className="text-3xl font-bold">{t("connect_with_mentor") || "Connect with your mentor"}</h1>
        </div>

        {info && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-3 rounded-lg bg-primary/10">
                  <UserRound className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle>{info.expert.name}</CardTitle>
                  <CardDescription>{info.segment.name}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {info.segment.description && (
                <p className="text-sm text-muted-foreground">{info.segment.description}</p>
              )}

              {info.segment.hasOnboarding && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-primary/5 border border-primary/10">
                  <Sparkles className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <p className="text-sm text-muted-foreground">
                    {t("mentor_onboarding_hint") ||
                      "Once you join, a personalized starting program will be waiting for you."}
                  </p>
                </div>
              )}

              {info.pricing?.priced && info.pricing.amount != null && (
                <div className="flex items-baseline justify-between rounded-lg border p-3">
                  <span className="text-sm text-muted-foreground">
                    {t("price") || "Price"}
                  </span>
                  <span className="text-xl font-semibold">
                    {new Intl.NumberFormat(undefined, {
                      style: "currency",
                      currency: info.pricing.currency || "USD",
                    }).format(info.pricing.amount)}
                  </span>
                </div>
              )}

              {!auth?.accessToken ? (
                isPaid ? (
                  <>
                    <p className="text-sm text-muted-foreground">
                      {t("guest_checkout_hint") ||
                        "Enter your details to continue to secure checkout. We'll set up your account and email you a link to choose a password."}
                    </p>
                    <div className="flex flex-col gap-3">
                      <Input
                        type="text"
                        placeholder={t("your_name") || "Your name"}
                        value={guestName}
                        onChange={(e) => setGuestName(e.target.value)}
                      />
                      <Input
                        type="email"
                        placeholder={t("your_email") || "Your email"}
                        value={guestEmail}
                        onChange={(e) => setGuestEmail(e.target.value)}
                      />
                      <Button onClick={handleGuestPay} disabled={guestPaying} className="w-full">
                        {guestPaying ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {t("starting_checkout") || "Starting checkout..."}
                          </>
                        ) : (
                          `${t("continue_to_checkout") || "Continue to Checkout"} · ${priceLabel}`
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => navigate(`/login?return=${encodeURIComponent(`/join-mentor/${token}`)}`)}
                        className="w-full"
                      >
                        {t("already_have_account") || "I already have an account"}
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">
                      {t("login_to_connect_mentor") ||
                        "Log in to connect with this mentor. New here? Create an account."}
                    </p>
                    <div className="flex flex-col gap-3">
                      <Button onClick={handleJoin} className="w-full">
                        {t("login_to_connect") || "Log In to Connect"}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => navigate(`/register?mentor=${token}`)}
                        className="w-full"
                      >
                        {t("sign_up_to_connect") || "Sign Up to Connect"}
                      </Button>
                    </div>
                  </>
                )
              ) : joined ? (
                <div className="text-center py-6">
                  <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto mb-3" />
                  <p className="font-semibold text-lg mb-2">
                    {t("connected") || "Connected!"}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t("redirecting") || "Taking you to your next step..."}
                  </p>
                </div>
              ) : (
                <>
                  {error && (
                    <div className="p-3 text-sm text-red-600 border border-red-200 rounded bg-red-50">
                      {error}
                    </div>
                  )}
                  <div className="flex flex-col gap-3">
                    {isPaid ? (
                      <Button onClick={handlePay} disabled={paying} className="w-full">
                        {paying ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {t("starting_checkout") || "Starting checkout..."}
                          </>
                        ) : (
                          `${t("pay_and_join") || "Pay & Join"} · ${priceLabel}`
                        )}
                      </Button>
                    ) : (
                      <Button onClick={handleJoin} disabled={joining} className="w-full">
                        {joining ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {t("connecting") || "Connecting..."}
                          </>
                        ) : (
                          t("connect_now") || "Connect Now"
                        )}
                      </Button>
                    )}
                    <Button variant="outline" onClick={() => navigate("/portal")} className="w-full">
                      {t("cancel") || "Cancel"}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {showPayment && paymentUrl && (
        <PaymentIframe
          open={showPayment}
          paymentUrl={paymentUrl}
          guestMode={isGuestPayment}
          onSuccess={() => {
            setShowPayment(false);
            if (returnUrl) {
              window.location.href = returnUrl + "?purchase=success";
              return;
            }
            if (isGuestPayment) {
              // Guest paid — account provisioned server-side; they set a password via email.
              setJoined(true);
              toast.success(
                t("check_email_to_finish") ||
                  "Payment complete! Check your email to set your password."
              );
            } else {
              setJoined(true);
              toast.success(
                t("successfully_connected_to_mentor") ||
                  "You're now connected with your mentor"
              );
              setTimeout(() => navigate("/portal"), 1200);
            }
          }}
          onError={(e) => {
            setShowPayment(false);
            toast.error(e || t("checkout_failed") || "Checkout failed");
          }}
          onClose={() => setShowPayment(false)}
        />
      )}
    </div>
  );
}

export default JoinMentor;
